import { color, Common, utils, Widget } from '@stockbit/ui';
import styled, { css } from 'styled-components';

const { Text, Flex, Input: InputDefault } = Common;
const { Table } = Widget;
const { hexOpacity } = utils;

interface OrderbookTextProps {
  first?: boolean;
  last?: boolean;
  bid?: boolean;
}

interface OrderbookTableWrapperProps {
  $isHidden?: boolean;
  $isIHSG?: boolean;
}

export const colorTextByType = {
  up: color.primary,
  down: color.sunsetOrange,
  notchange: color.highball,
  neutral: color.suvaGrey,
};

export const OrderbookIndexSummary = styled.div`
  border-bottom: 1px solid ${color.gainsboro};
  background-color: ${color.light};
  display: block;
  padding: 10px;

  > span {
    font-variant: normal;
    -webkit-font-feature-settings: normal;
    font-feature-settings: normal;
    line-height: 1.15;
  }
`;

export const OrderbookText = styled(Text).attrs((props) => ({
  size: '12.5px',
  align: 'right',
  padding: props.padding || '4px 5px',
  height: '1.15',
  type: props.type,
}))`
  color: ${({ type }) => colorTextByType[type]};

  ${({ first = false, last = false }: OrderbookTextProps) => {
    if (first) {
      return css`
        padding-left: 15px;
      `;
    }
    if (last) {
      return css`
        padding-right: 15px;
      `;
    }

    return '';
  }}
`;

export const OrderbookTitle = styled(OrderbookText).attrs({
  family: 'bold',
})`
  background: ${color.aliceBlue};
  border-top: 1px solid ${color.gainsboro};
  border-bottom: 1px solid ${color.gainsboro};
  font-weight: normal;

  ${({ first = false, last = false }: OrderbookTextProps) => {
    if (first) {
      return css`
        padding-left: 15px;
      `;
    }
    if (last) {
      return css`
        padding-right: 15px;
      `;
    }

    return '';
  }}
`;

export const OrderbookTextHover = styled(OrderbookText)
  .withConfig({
    shouldForwardProp: (prop: string) => !['bid'].includes(prop),
  })
  .attrs((props): OrderbookTextProps => ({ bid: props.bid || false }))`
  cursor: pointer;
  :hover {
    background: ${({ bid = false }: OrderbookTextProps) =>
    (bid ? color['primary30%'] : color['sunsetOrange30%'])};
  }
`;

export const SummaryCell = styled(Text).attrs({
  size: '12.5px',
  weight: 'normal',
  height: '26px',
  p: '0px 10px',
  family: 'bold',
})`
  background-color: ${color.aliceBlue};
  border-top: 1px solid ${color.gainsboro};
`;

// Header

export const HeaderContainer = styled(Flex).attrs({
  align: 'center',
  px: '10px',
})`
  position: relative;
  background: ${color.aliceBlue};
  height: 40px;
  cursor: pointer;
  border-bottom: 1px solid ${color.gainsboro};

  & .ant-select-dropdown {
    top: 4px !important;
  }
`;

export const Input = styled(InputDefault)
  .withConfig({
    shouldForwardProp: (prop: string) => !['active'].includes(prop),
  })
  .attrs((props) => ({ height: '30px', active: props.active }))`
  position: absolute;
  bottom: 4px;
  left: 0;
  transition: 0.3s ease;
  ${({ active }) =>
    (active
      ? css`
          padding: 5px;
          border: 1px solid ${color.gainsboro};
        `
      : css`
          padding: 0;
          border: none;
        `)}
`;

export const Tag = styled(Text).attrs({
  color: color.light,
  weight: 'bold',
  px: '3px',
})`
  background: ${color.dimgray};
  border-radius: 2px;
  font-size: 12px;
`;

export const SymbolSearch = styled(Text)
  .withConfig({
    // @ts-ignore
    shouldForwardProp: (prop) => !['active'].includes(prop),
  })
  .attrs((props) => ({
    p: '5px 10px',
    mr: '10px',
    active: props.active,
  }))`
  display: ${({ active }) => (active ? 'block' : 'none')};
  border: 1px solid ${color.gainsboro};
  background: ${color.light};
  cursor: text;
`;

export const PriceChangeText = styled(Text).attrs({
  size: '18px',
})`
  display: inline-block;
  span {
    color: black;
  }

  ${(props) => props.$type &&
    css`
      color: ${colorTextByType[props.$type]};
    `
}
`;

export const TableOrderlist = styled(Table)`
  margin-top: 10px;

  .ant-table-thead {
    th {
      width: calc((100% - 20px) / 4);
    }
  }

  .ant-table-tbody {
    > tr:hover {
      > td {
        background: unset;
      }
    }

    td {
      width: calc((100% - 20px) / 4);
      white-space: nowrap;
    }

    tr:first-child {
      > td p {
        padding-top: 10px;
      }
    }

    tr:last-child {
      > td p {
        padding-bottom: 10px;
      }
    }
  }
`;

export const TableOHLC = styled(Table)`
  .ant-table-tbody {
    > tr:hover {
      > td {
        background: unset;
      }
    }

    td {
      white-space: nowrap;
    }
  }
`;

export const TagDesc = styled(Text)`
  font-size: 13px;
  margin-left: 10px;
  max-width: calc(100% - 74px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OrderbookTableWrapper = styled.div<OrderbookTableWrapperProps>`
  user-select: none;
  position: relative;

  table {
    margin: auto;
    position: relative;
    table-layout: fixed;
    width: 100%;
    z-index: 1;
  }

  tbody {
    display: block;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 255px;
  }

  thead, tbody tr, tfoot {
    display: table;
    width: 329px;
    table-layout: fixed;
  }

  thead {
    & th.title {
      height: 26px !important;
      line-height: 16px;
      font-weight: normal;
      text-align: center;
      padding: 5px 0px !important;
    }
  }

  tbody {
    > tr:hover {
      > td {
        background: unset;
      }
    }
    
    td {
      text-align: center;
      padding: 5px 0px;
      white-space: nowrap;
    }

    td:first-child, td:last-child {
      width: 40px;
    }

    td:nth-child(2), td:nth-child(5) {
      width: 66.5px;
    }

    td:nth-child(3), td:nth-child(4) {
      width: 50px;
    }
  }

  tfoot {
    background: ${color.aliceBlue};
    border-top: 1px solid ${color.gainsboro};
    border-bottom: 1px solid ${color.gainsboro};

    > tr {
      > td {
        border-bottom: none;
        height: 26px;
        line-height: 16px;
        font-weight: normal;
        text-align: center;
        padding: 5px 0px;
      }
    }

    td:first-child, td:last-child {
      width: 40px;
    }

    td:nth-child(2), td:nth-child(5) {
      width: 66.5px;
    }
  }

  tr.blurred {
    opacity: 0.4;
  }

  .highlited-bid::before,
  .highlited-off::before {
    position: absolute;
    content: '';
    width: 200%;
    height: 100%;
  }

  .highlited-bid::before {
    background: ${color.primary + hexOpacity(30)};
  }

  td:nth-child(2):before {
    left: -50px;
  }

  .highlited-off::before {
    background: ${color.sunsetOrange + hexOpacity(30)};
  }

  td:nth-child(4):before {
    right: -5px;
  }

  ${(props) =>
    props.$isHidden &&
    css`
      padding-top: 25px;

      ::before {
        border-bottom: none;
      }
    `}

  ${(props) =>
    props.$isIHSG &&
    css`
      ::after,
      ::before {
        display: none;
      }
    `}
`;

export const Wrapper = styled.div`
  background-color: ${color.light};
  position: relative;
  z-index: 99;

  table.table-live-price {
    td:first-child {
      padding-left: 0px;
      text-align: left;
      width: 30px;
    }

    td:nth-child(even) {
      padding-right: 17px;

      &.two-column {
        padding-right: 18px;
      }
    }

    td:nth-child(2) {
      width: 68.5px;

      &.two-column {
        padding-left: 8px;
        width: 126.5px;
      }
    }

    td:nth-child(3) {
      padding-left: 17px;
      text-align: left;
      width: 47px;

      &.two-column {
        padding-left: 18px;
        padding-right: 8px;
        width: 56px;
      }
    }

    td:nth-child(4) {
      width: 69px;
    }

    td:nth-child(5) {
      padding-left: 17px;
      text-align: left;
      width: 47px;
    }

    td:last-child {
      padding-right: 0px; 
      width: 51.5px;
    }
  }

  thead th.title {
    height: 25px !important;
    padding: 5px 0px !important;
  }
`;
